<script setup lang="ts">
interface Props {
  name: string;
  modelValue: boolean;
  /**
   * Make the input fully controlled: the DOM never diverges from
   * `modelValue`.
   *
   * A click flips the native `checked` property before Vue sees anything.
   * When the parent then does NOT change `modelValue` — an async write that
   * failed or was dropped by an in-flight guard — the bound prop is
   * unchanged, so Vue has nothing to patch and the browser's optimistic flip
   * sticks: a check mark that exists nowhere but the DOM (WI #5646). Unlike
   * `v-model`, a plain `:checked` binding has no force-resync hook, so this
   * flag reverts the DOM to the current prop and lets the parent decide
   * whether the new value sticks.
   *
   * Opt-in, because several consumers (activity documents/links, company
   * tags) update `modelValue` only after a server round-trip and rely on the
   * browser's optimistic flip as their only pending feedback — reverting it
   * there would read as an unresponsive click. Turn this on wherever the
   * parent can reject or drop a toggle AND shows its own pending state.
   */
  controlled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  controlled: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

function onInput(event: Event) {
  if (props.controlled) {
    (event.target as HTMLInputElement).checked = props.modelValue;
  }
  emit('update:modelValue', !props.modelValue);
}
</script>

<template>
  <input
    type="checkbox"
    :value="name"
    :checked="props.modelValue"
    @input="onInput"
  />
</template>

<style lang="scss" scoped>
input[type='checkbox'] {
  text-align: center;
  width: 20px !important;
  height: 20px !important;
  appearance: none;
  box-shadow: none;
  font-size: 1em;
  border-radius: var(--border-radius-4);
  border: 1px solid var(--color-gray-300);
  cursor: pointer;
  flex-shrink: 0;
}

input[type='checkbox']:checked {
  border: 1px solid var(--color-primary-100);
  background-color: var(--color-primary-400);
  animation: checkbox-animation 0.4s;
}

input[type='checkbox']:checked:after {
  content: '\2713';
  color: var(--color-primary-25);
  font-weight: var(--font-weight-bold);
  animation: scale-up-center 0.5s;
  opacity: 1;
}

input[type='checkbox']:disabled {
  border: 1px solid var(--color-gray-200);
  cursor: not-allowed;
}
</style>
